
import React, { useState, useEffect } from 'react';

const RiskAssessmentChart = ({
  percentage = 50,
  title = "Risk Assessment",
  description = "Lithium is rated 90 out of 100 for its essentiality. It's critical for all modern electronics and the electric vehicle industry",
  label = "Risk",
  showLabels = true,
  loading = false,
  error = null
}) => {
  const [displayPercentage, setDisplayPercentage] = useState(0);

  // Animate the displayed percentage number
  useEffect(() => {
    const startValue = 0;
    const endValue = percentage;
    const duration = 800;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (endValue - startValue) * easeOutCubic;
      setDisplayPercentage(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  }, [percentage]);

  // Arc properties - partial circle (270 degrees)
  const size = 150;
  const strokeWidth = 16;
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;
  
  // Calculate the arc path (270 degrees, starting from top-left)
  const startAngle = -45 * (Math.PI / 180); // Convert to radians
  const endAngle = startAngle + (270 * Math.PI / 180); // 270 degrees total
  
  // Create the full arc path
  const startX = centerX + radius * Math.cos(startAngle);
  const startY = centerY + radius * Math.sin(startAngle);
  const endX = centerX + radius * Math.cos(endAngle);
  const endY = centerY + radius * Math.sin(endAngle);
  
  const arcPath = `M ${startX} ${startY} A ${radius} ${radius} 0 1 1 ${endX} ${endY}`;
  
  // Calculate path length for stroke-dasharray animation
  const arcLength = (270 / 360) * (2 * Math.PI * radius);
  const strokeDasharray = arcLength;
  const strokeDashoffset = arcLength - (percentage / 100) * arcLength;

  const svg = (
    <svg width={size} height={size} className="risk-assessment-svg" style={{ transform: 'rotate(180deg)' }}>
        <path
            d={arcPath}
            stroke="#374151"
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
        />

        <path
            d={arcPath}
            stroke="#A855F7"
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            className="risk-assessment-progress-arc"
        />
    </svg>
);

// Show loading state
if (loading) {  return (
      <div className="risk-assessment-chart-container">
        <div className="risk-assessment-chart-wrapper">
          <div className="risk-assessment-chart-svg-container">
            <div className="risk-assessment-center-text">
              <div className="risk-assessment-percentage">--</div>
              <div className="risk-assessment-label">Loading...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="risk-assessment-chart-container">
        <div className="risk-assessment-chart-wrapper">
          <div className="risk-assessment-chart-svg-container">
            <div className="risk-assessment-center-text">
              <div className="risk-assessment-percentage">!</div>
              <div className="risk-assessment-label">Error</div>
            </div>
          </div>
        </div>
        {showLabels && (
          <p className="risk-assessment-description" style={{ color: '#ef4444' }}>
            Failed to load assessment data
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="risk-assessment-chart-container">
        <div className="risk-assessment-chart-wrapper">
            <div className="risk-assessment-chart-svg-container">
                {svg}
                <div className="risk-assessment-center-text">
                    <div className="risk-assessment-percentage">
                        {Math.round(displayPercentage)}%
                    </div>
                    <div className="risk-assessment-label">{label}</div>
                </div>
            </div>
        </div>

        {showLabels && (
            <>
                <h2 className="risk-assessment-title">
                    {title}
                </h2>

                <p className="risk-assessment-description">
                    {description}
                </p>
            </>
        )}
    </div>
);
};
export default RiskAssessmentChart;